import { createPopper } from "@popperjs/core"

import "./stars-bar.scss"

export const ACTIVE_CLASSNAME = "stars-bar__star--is-active"
export const FORM_CLASSNAME = "js-stars-bar-form"

/**
 * @export
 * @param {HTMLElement} starsBar
 * @param {object} settings
 * @param {number} settings.activeStars
 */
export function updateStarsBar(starsBar, { activeStars }) {
  /** @type {Array<SVGAElement>} */
  const svgElements = starsBar.querySelectorAll("svg")

  if (svgElements.length < 5) return

  Array.from(svgElements).forEach((svg, i) => {
    svg.classList.toggle(ACTIVE_CLASSNAME, activeStars > i)
  })
}

/**
 * @export
 * @param {HTMLElement} starsBar
 * @param {object} settings
 * @param {string} settings.url
 */
export function updateFormUrl(starsBar, { url }) {
  /** @type {HTMLFormElement} */
  const form = starsBar.querySelector(`.${FORM_CLASSNAME}`)

  form.action = url
}

/**
 * @param {HTMLElement} target
 * @param {Number} rating
 * @param {function(number) : void} onConfirm
 */
function createAndAppendPromptTooltip(target, rating, onConfirm) {
  const tooltip = document.createElement("div")
  tooltip.classList.add("tooltip", "is-opaque", "is-prompt", "is-small", "has-arrow-down")

  const confirmationLine = document.createElement("div")
  confirmationLine.classList.add("has-mb-2")
  confirmationLine.appendChild(document.createTextNode(`Give this photo ${rating}/5?`))
  tooltip.appendChild(confirmationLine)

  const controls = document.createElement("div")
  controls.classList.add("is-flex", "has-jc-sb")

  const agreedButton = document.createElement("button")
  agreedButton.classList.add("c-button", "is-small", "is-fullwidth", "is-primary", "has-mr-2")
  agreedButton.appendChild(document.createTextNode("Vote"))
  controls.appendChild(agreedButton)

  const cancelButton = document.createElement("button")
  cancelButton.classList.add("c-button", "is-small", "is-fullwidth")
  cancelButton.appendChild(document.createTextNode("Cancel"))
  controls.appendChild(cancelButton)

  tooltip.appendChild(controls)

  document.body.appendChild(tooltip)

  const instance = createPopper(target, tooltip, {
    placement: "top",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 0],
        },
      },
    ],
    onFirstUpdate: () => agreedButton.focus(),
  })

  function dispose() {
    instance.destroy()
    tooltip.remove()
  }

  /**
   * @param {MouseEvent} event
   */
  function globalClickListenerOnce(event) {
    if (!tooltip.contains(event.target)) {
      dispose()
      document.body.removeEventListener("click", globalClickListenerOnce)
    }
  }

  document.body.addEventListener("click", globalClickListenerOnce)
  agreedButton.addEventListener("click", () => {
    onConfirm(rating)
    dispose()
  })
  cancelButton.addEventListener("click", dispose)
}

/**
 * @param {HTMLFormElement} form
 * @param {function(number) : void} onChange
 */
function listenToChange(form, onChange) {
  form.addEventListener("change", (event) => {
    const radioButton = event.target
    if (radioButton.tagName === "INPUT") {
      const rating = Number(radioButton.value)
      createAndAppendPromptTooltip(radioButton, rating, onChange)
    }
  })
}

/**
 * @export
 * @param {HTMLElement} starsBar
 * @param {function(number?) : void} onSubmit
 * @returns {function() : void} remove listener
 */
export function listenToSubmit(starsBar, onSubmit) {
  /** @type {HTMLFormElement} */
  const form = starsBar.querySelector(`.${FORM_CLASSNAME}`)

  listenToChange(form, onSubmit)

  function listener(event) {
    event.preventDefault()

    const ratingData = new FormData(form).get("photo_vote[rating]")
    if (ratingData == null) {
      onSubmit(null)
      return
    }

    onSubmit(Number(ratingData))
  }

  form.addEventListener("submit", listener)

  return () => form.removeEventListener("submit", listener)
}
